import React, { useState, useEffect } from 'react';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  RectangleGroupIcon,
  ListBulletIcon
} from '@heroicons/react/24/outline';
import api from '../../utils/api';

const ClientCustomFields = () => {
  const [activeTab, setActiveTab] = useState('fields');
  const [customFields, setCustomFields] = useState([]);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sectionFilter, setSectionFilter] = useState('all');
  
  // Field form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [formData, setFormData] = useState({
    field_name: '',
    display_label: '',
    field_type: 'text',
    field_options: {},
    is_required: false,
    is_active: true,
    sort_order: 0,
    help_text: '',
    section_id: null
  });

  // Section form state
  const [showAddSectionForm, setShowAddSectionForm] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [sectionFormData, setSectionFormData] = useState({
    section_name: '',
    display_label: '',
    description: '',
    icon: '',
    is_collapsible: true,
    is_expanded_by_default: true,
    is_required: false,
    section_order: 0,
    is_active: true
  });

  const fieldTypes = [
    { value: 'text', label: 'Text' },
    { value: 'number', label: 'Number' },
    { value: 'date', label: 'Date' },
    { value: 'boolean', label: 'Yes/No' },
    { value: 'select', label: 'Dropdown' }
  ];

  const tabs = [
    { id: 'fields', name: 'Custom Fields', icon: ListBulletIcon },
    { id: 'sections', name: 'Sections', icon: RectangleGroupIcon }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchCustomFields(),
        fetchSections()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomFields = async () => {
    try {
      const response = await api.get('/custom-fields/entity/client?includeInactive=true');
      setCustomFields(response.data);
    } catch (error) {
      console.error('Error fetching custom fields:', error);
      throw error;
    }
  };

  const fetchSections = async () => {
    try {
      const response = await api.get('/custom-fields/sections/entity/client?includeInactive=true');
      setSections(response.data);
    } catch (error) {
      console.error('Error fetching sections:', error);
      throw error;
    }
  };

  // ===== FIELD MANAGEMENT =====

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setError('');
      
      // Prepare the data
      const submitData = {
        ...formData,
        entity_type: 'client',
        field_options: formData.field_type === 'select' ? 
          { options: formData.selectOptions || [] } : 
          formData.field_options
      };

      if (editingField) {
        await api.put(`/custom-fields/${editingField.id}`, submitData);
      } else {
        await api.post('/custom-fields', submitData);
      }

      await fetchCustomFields();
      resetForm();
    } catch (error) {
      console.error('Error saving custom field:', error);
      setError(error.response?.data?.error || 'Failed to save custom field');
    }
  };

  const handleDelete = async (fieldId) => {
    if (!window.confirm('Are you sure you want to deactivate this custom field?')) {
      return;
    }

    try {
      await api.delete(`/custom-fields/${fieldId}`);
      await fetchCustomFields();
    } catch (error) {
      console.error('Error deleting custom field:', error);
      setError('Failed to delete custom field');
    }
  };

  const handleToggleActive = async (field) => {
    try {
      await api.put(`/custom-fields/${field.id}`, {
        is_active: !field.is_active
      });
      await fetchCustomFields();
    } catch (error) {
      console.error('Error toggling field status:', error);
      setError('Failed to update field status');
    }
  };

  const handleReorder = async (fieldId, direction) => {
    const field = customFields.find(f => f.id === fieldId);
    const newSortOrder = direction === 'up' ? field.sort_order - 1 : field.sort_order + 1;
    
    try {
      await api.put(`/custom-fields/${fieldId}`, {
        sort_order: newSortOrder
      });
      await fetchCustomFields();
    } catch (error) {
      console.error('Error reordering field:', error);
      setError('Failed to reorder field');
    }
  };

  const resetForm = () => {
    setFormData({
      field_name: '',
      display_label: '',
      field_type: 'text',
      field_options: {},
      is_required: false,
      is_active: true,
      sort_order: 0,
      help_text: '',
      section_id: null
    });
    setEditingField(null);
    setShowAddForm(false);
  };

  const startEdit = (field) => {
    setFormData({
      field_name: field.field_name,
      display_label: field.display_label,
      field_type: field.field_type,
      field_options: field.field_options || {},
      is_required: field.is_required,
      is_active: field.is_active,
      sort_order: field.sort_order,
      help_text: field.help_text || '',
      section_id: field.section_id || null,
      selectOptions: field.field_options?.options || []
    });
    setEditingField(field);
    setShowAddForm(true);
  };

  const handleSelectOptionsChange = (options) => {
    setFormData(prev => ({
      ...prev,
      selectOptions: options
    }));
  };

  // ===== SECTION MANAGEMENT =====

  const handleSectionSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setError('');
      
      const submitData = {
        ...sectionFormData,
        entity_type: 'client'
      };

      if (editingSection) {
        await api.put(`/custom-fields/sections/${editingSection.id}`, submitData);
      } else {
        await api.post('/custom-fields/sections', submitData);
      }

      await fetchSections();
      resetSectionForm();
    } catch (error) {
      console.error('Error saving section:', error);
      setError(error.response?.data?.error || 'Failed to save section');
    }
  };

  const handleSectionDelete = async (sectionId) => {
    if (!window.confirm('Are you sure you want to deactivate this section?')) {
      return;
    }

    try {
      await api.delete(`/custom-fields/sections/${sectionId}`);
      await fetchSections();
    } catch (error) {
      console.error('Error deleting section:', error);
      setError('Failed to delete section');
    }
  };

  const handleSectionToggleActive = async (section) => {
    try {
      await api.put(`/custom-fields/sections/${section.id}`, {
        is_active: !section.is_active
      });
      await fetchSections();
    } catch (error) {
      console.error('Error toggling section status:', error);
      setError('Failed to update section status');
    }
  };

  const handleSectionReorder = async (sectionId, direction) => {
    const section = sections.find(s => s.id === sectionId);
    const newSectionOrder = direction === 'up' ? section.section_order - 1 : section.section_order + 1;
    
    try {
      await api.put(`/custom-fields/sections/${sectionId}`, {
        section_order: newSectionOrder
      });
      await fetchSections();
    } catch (error) {
      console.error('Error reordering section:', error);
      setError('Failed to reorder section');
    }
  };

  const resetSectionForm = () => {
    setSectionFormData({
      section_name: '',
      display_label: '',
      description: '',
      icon: '',
      is_collapsible: true,
      is_expanded_by_default: true,
      is_required: false,
      section_order: 0,
      is_active: true
    });
    setEditingSection(null);
    setShowAddSectionForm(false);
  };

  const startSectionEdit = (section) => {
    setSectionFormData({
      section_name: section.section_name,
      display_label: section.display_label,
      description: section.description || '',
      icon: section.icon || '',
      is_collapsible: section.is_collapsible,
      is_expanded_by_default: section.is_expanded_by_default,
      is_required: section.is_required,
      section_order: section.section_order,
      is_active: section.is_active
    });
    setEditingSection(section);
    setShowAddSectionForm(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Client Custom Fields</h2>
          <p className="text-gray-600 mt-1">
            Configure custom fields and sections that will appear on client records
          </p>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`${
                  activeTab === tab.id
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
              >
                <Icon className="h-5 w-5 mr-2" />
                {tab.name}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'fields' && (
        <div className="space-y-6">
          {/* Add Field Button */}
          <div className="flex justify-end">
            <button
              onClick={() => setShowAddForm(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Add Custom Field
            </button>
          </div>

          {/* Section Filter */}
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <label htmlFor="section-filter" className="text-sm font-medium text-gray-700">
                Filter by Section:
              </label>
              <select
                id="section-filter"
                value={sectionFilter}
                onChange={(e) => setSectionFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              >
                <option value="all">All Sections</option>
                <option value="no-section">No Section</option>
                {sections
                  .filter(s => s.is_active)
                  .sort((a, b) => a.display_label.localeCompare(b.display_label))
                  .map(section => (
                    <option key={section.id} value={section.id}>
                      {section.display_label}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          {/* Add/Edit Field Form */}
          {showAddForm && (
            <div className="bg-white shadow rounded-lg p-6 border">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingField ? 'Edit Custom Field' : 'Add Custom Field'}
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Field Name *
                    </label>
                    <input
                      type="text"
                      value={formData.field_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, field_name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="annual_revenue"
                      required
                      disabled={editingField} // Don't allow changing field name on edit
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Internal name (letters, numbers, underscores only)
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Display Label *
                    </label>
                    <input
                      type="text"
                      value={formData.display_label}
                      onChange={(e) => setFormData(prev => ({ ...prev, display_label: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Annual Revenue"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Field Type *
                    </label>
                    <select
                      value={formData.field_type}
                      onChange={(e) => setFormData(prev => ({ ...prev, field_type: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    >
                      {fieldTypes.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Section
                    </label>
                    <select
                      value={formData.section_id || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, section_id: e.target.value || null }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="">No Section</option>
                      {sections.filter(s => s.is_active).map(section => (
                        <option key={section.id} value={section.id}>
                          {section.display_label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Sort Order
                    </label>
                    <input
                      type="number"
                      value={formData.sort_order}
                      onChange={(e) => setFormData(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      min="0"
                    />
                  </div>
                </div>

                {/* Dropdown Options */}
                {formData.field_type === 'select' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Dropdown Options
                    </label>
                    <SelectOptionsEditor
                      options={formData.selectOptions || []}
                      onChange={handleSelectOptionsChange}
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Help Text
                  </label>
                  <textarea
                    value={formData.help_text}
                    onChange={(e) => setFormData(prev => ({ ...prev, help_text: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    rows="2"
                    placeholder="Optional help text for users"
                  />
                </div>

                <div className="flex items-center space-x-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_required}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_required: e.target.checked }))}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">Required field</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">Active</span>
                  </label>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    {editingField ? 'Update' : 'Create'} Field
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Custom Fields List */}
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Custom Fields</h3>
            </div>
            
            {customFields.length === 0 ? (
              <div className="px-6 py-8 text-center">
                <p className="text-gray-500">No custom fields configured yet.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Field
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Section
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Order
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {customFields
                      .filter(field => {
                        if (sectionFilter === 'all') return true;
                        if (sectionFilter === 'no-section') return !field.section_id;
                        return field.section_id === parseInt(sectionFilter);
                      })
                      .sort((a, b) => {
                        // First sort by section (No Section goes to the end)
                        const sectionA = a.section_display_label || 'zzz_No Section';
                        const sectionB = b.section_display_label || 'zzz_No Section';
                        const sectionCompare = sectionA.localeCompare(sectionB);
                        
                        // If sections are the same, sort by sort_order
                        if (sectionCompare === 0) {
                          return a.sort_order - b.sort_order;
                        }
                        
                        return sectionCompare;
                      })
                      .map((field) => (
                      <tr key={field.id} className={!field.is_active ? 'opacity-50' : ''}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {field.display_label}
                            </div>
                            <div className="text-sm text-gray-500">
                              {field.field_name}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {fieldTypes.find(t => t.value === field.field_type)?.label || field.field_type}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {field.section_display_label || 'No Section'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="flex items-center space-x-1">
                            <span>{field.sort_order}</span>
                            <div className="flex flex-col">
                              <button
                                onClick={() => handleReorder(field.id, 'up')}
                                className="text-gray-400 hover:text-gray-600"
                              >
                                <ArrowUpIcon className="h-3 w-3" />
                              </button>
                              <button
                                onClick={() => handleReorder(field.id, 'down')}
                                className="text-gray-400 hover:text-gray-600"
                              >
                                <ArrowDownIcon className="h-3 w-3" />
                              </button>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              field.is_active 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {field.is_active ? 'Active' : 'Inactive'}
                            </span>
                            {field.is_required && (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                Required
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => startEdit(field)}
                              className="text-indigo-600 hover:text-indigo-900"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleToggleActive(field)}
                              className="text-gray-600 hover:text-gray-900"
                            >
                              {field.is_active ? (
                                <EyeSlashIcon className="h-4 w-4" />
                              ) : (
                                <EyeIcon className="h-4 w-4" />
                              )}
                            </button>
                            <button
                              onClick={() => handleDelete(field.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'sections' && (
        <div className="space-y-6">
          {/* Add Section Button */}
          <div className="flex justify-end">
            <button
              onClick={() => setShowAddSectionForm(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Add Section
            </button>
          </div>

          {/* Add/Edit Section Form */}
          {showAddSectionForm && (
            <div className="bg-white shadow rounded-lg p-6 border">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingSection ? 'Edit Section' : 'Add Section'}
              </h3>
              
              <form onSubmit={handleSectionSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Section Name *
                    </label>
                    <input
                      type="text"
                      value={sectionFormData.section_name}
                      onChange={(e) => setSectionFormData(prev => ({ ...prev, section_name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="financial_info"
                      required
                      disabled={editingSection} // Don't allow changing section name on edit
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Internal name (letters, numbers, underscores only)
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Display Label *
                    </label>
                    <input
                      type="text"
                      value={sectionFormData.display_label}
                      onChange={(e) => setSectionFormData(prev => ({ ...prev, display_label: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Financial Information"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Icon (optional)
                    </label>
                    <input
                      type="text"
                      value={sectionFormData.icon}
                      onChange={(e) => setSectionFormData(prev => ({ ...prev, icon: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="CurrencyDollarIcon"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Section Order
                    </label>
                    <input
                      type="number"
                      value={sectionFormData.section_order}
                      onChange={(e) => setSectionFormData(prev => ({ ...prev, section_order: parseInt(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      min="0"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={sectionFormData.description}
                    onChange={(e) => setSectionFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    rows="2"
                    placeholder="Optional description for this section"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={sectionFormData.is_collapsible}
                        onChange={(e) => setSectionFormData(prev => ({ ...prev, is_collapsible: e.target.checked }))}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">Collapsible</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={sectionFormData.is_expanded_by_default}
                        onChange={(e) => setSectionFormData(prev => ({ ...prev, is_expanded_by_default: e.target.checked }))}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        disabled={!sectionFormData.is_collapsible}
                      />
                      <span className="ml-2 text-sm text-gray-700">Expanded by default</span>
                    </label>
                  </div>

                  <div className="flex items-center space-x-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={sectionFormData.is_required}
                        onChange={(e) => setSectionFormData(prev => ({ ...prev, is_required: e.target.checked }))}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">Required section</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={sectionFormData.is_active}
                        onChange={(e) => setSectionFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">Active</span>
                    </label>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={resetSectionForm}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    {editingSection ? 'Update' : 'Create'} Section
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Sections List */}
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Sections</h3>
            </div>
            
            {sections.length === 0 ? (
              <div className="px-6 py-8 text-center">
                <p className="text-gray-500">No sections configured yet.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Section
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Fields
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Order
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Settings
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {sections
                      .sort((a, b) => a.display_label.localeCompare(b.display_label))
                      .map((section) => (
                      <tr key={section.id} className={!section.is_active ? 'opacity-50' : ''}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {section.display_label}
                            </div>
                            <div className="text-sm text-gray-500">
                              {section.section_name}
                            </div>
                            {section.description && (
                              <div className="text-xs text-gray-400 mt-1">
                                {section.description}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            {section.field_count} fields
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="flex items-center space-x-1">
                            <span>{section.section_order}</span>
                            <div className="flex flex-col">
                              <button
                                onClick={() => handleSectionReorder(section.id, 'up')}
                                className="text-gray-400 hover:text-gray-600"
                              >
                                <ArrowUpIcon className="h-3 w-3" />
                              </button>
                              <button
                                onClick={() => handleSectionReorder(section.id, 'down')}
                                className="text-gray-400 hover:text-gray-600"
                              >
                                <ArrowDownIcon className="h-3 w-3" />
                              </button>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-wrap gap-1">
                            {section.is_collapsible && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                Collapsible
                              </span>
                            )}
                            {section.is_expanded_by_default && section.is_collapsible && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                Expanded
                              </span>
                            )}
                            {section.is_required && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                                Required
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            section.is_active 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {section.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => startSectionEdit(section)}
                              className="text-indigo-600 hover:text-indigo-900"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleSectionToggleActive(section)}
                              className="text-gray-600 hover:text-gray-900"
                            >
                              {section.is_active ? (
                                <EyeSlashIcon className="h-4 w-4" />
                              ) : (
                                <EyeIcon className="h-4 w-4" />
                              )}
                            </button>
                            <button
                              onClick={() => handleSectionDelete(section.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Component for editing select dropdown options
const SelectOptionsEditor = ({ options, onChange }) => {
  const [newOption, setNewOption] = useState('');

  const addOption = () => {
    if (newOption.trim() && !options.includes(newOption.trim())) {
      onChange([...options, newOption.trim()]);
      setNewOption('');
    }
  };

  const removeOption = (index) => {
    onChange(options.filter((_, i) => i !== index));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addOption();
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex space-x-2">
        <input
          type="text"
          value={newOption}
          onChange={(e) => setNewOption(e.target.value)}
          onKeyPress={handleKeyPress}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          placeholder="Add option..."
        />
        <button
          type="button"
          onClick={addOption}
          className="px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          Add
        </button>
      </div>
      
      {options.length > 0 && (
        <div className="space-y-1">
          {options.map((option, index) => (
            <div key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded">
              <span className="text-sm">{option}</span>
              <button
                type="button"
                onClick={() => removeOption(index)}
                className="text-red-600 hover:text-red-800"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ClientCustomFields; 